import {
  FilterBody,
  FilterQuery,
  ProjectFields,
  SortOption
} from '@yes.technology/react-toolkit'
import { merge } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import useDeviceType from 'shared/hooks/useDeviceType'
import deepOmit from 'shared/utils/object/deepOmit'
import { useFetchYesObjects } from 'yesObject/shared'
import {
  baseProps,
  defaultEventsProjectFileds,
  defaultPageSizeByDevice
} from '../contants'
import { PageSizeByDeviceProps } from '../EventCardContainer.types'
import getClonedFilterquery from '../utils/getClonedFilterquery'
import getDisabledArrow from '../utils/getDisabledArrow'
import getFilteredStartDate from '../utils/getFilteredStartDate'

type UsePaginatedMonthlyEventsProps = {
  elementUuid: string
  startDate: string
  pageSize?: number
  filterquery: FilterQuery
  filterFieldsFilterQuery: FilterQuery
  objectclassUuid: string
  projectFields?: ProjectFields
  pageSizeByDevice?: PageSizeByDeviceProps
  orderby?: SortOption[]
}

type GetEventsWithinAMonthFilterBodyProps = {
  filterquery: FilterQuery
  filterFieldsFilterQuery: FilterQuery
  objectclassUuid: string
  dateString: string
}

const getEventsWithinAMonthFilterBody = ({
  filterquery,
  filterFieldsFilterQuery,
  objectclassUuid,
  dateString
}: GetEventsWithinAMonthFilterBodyProps): FilterBody | null => {
  if (!dateString) {
    return null
  }

  const date = new Date(dateString)

  const startOfMonthDate = new Date(date.getFullYear(), date.getMonth(), 1)
  const endOfMonthDate = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  const startOfNextMonthDate = new Date(endOfMonthDate)
  startOfNextMonthDate.setDate(endOfMonthDate.getDate() + 1)
  startOfNextMonthDate.setHours(0, 0, 0, 0)

  const startOfMonth = startOfMonthDate.toISOString()

  const endOfMonth = startOfNextMonthDate.toISOString()

  const { operator, filteredStartDate } = getFilteredStartDate(
    filterFieldsFilterQuery
  )

  return {
    ...baseProps,
    objectclass: objectclassUuid,
    skip_relations: false,
    query: {
      ...merge(
        getClonedFilterquery(filterquery),
        deepOmit(filterFieldsFilterQuery ?? {}, 'start_date.des'),
        {
          $and: [
            merge(
              {
                'start_date.des': {
                  $gte: startOfMonth,
                  $lt: endOfMonth
                }
              },
              filterFieldsFilterQuery && filteredStartDate
                ? {
                    'start_date.des': {
                      [operator]: filteredStartDate
                    }
                  }
                : {}
            )
          ]
        }
      )
    }
  }
}

const usePaginatedMonthlyEvents = ({
  startDate,
  filterquery,
  filterFieldsFilterQuery,
  objectclassUuid,
  projectFields = defaultEventsProjectFileds,
  pageSizeByDevice = defaultPageSizeByDevice,
  orderby = [
    {
      start_date: 'asc'
    }
  ]
}: UsePaginatedMonthlyEventsProps) => {
  const { t } = useTranslation()

  const deviceType = useDeviceType()

  const pageSize = pageSizeByDevice[deviceType] || 9

  const [totalPages, setTotalPages] = useState<number>(1)

  const filterBody = getEventsWithinAMonthFilterBody({
    filterquery,
    filterFieldsFilterQuery,
    objectclassUuid,
    dateString: startDate
  })

  const {
    isLoading,
    yesObjects: data,
    pagination,
    setPage,
    setPagination
  } = useFetchYesObjects({
    filterBody: {
      ...filterBody,
      project_fields: projectFields
    } as FilterBody,
    initialPagination: {
      limit: pageSize,
      offset: 0
    },
    initialSortOptions: orderby,
    showLoading: false,
    enabled: !!objectclassUuid && !!filterBody
  })

  useEffect(() => {
    setPage(1)
  }, [setPage, deviceType, startDate, filterFieldsFilterQuery])

  useEffect(() => {
    if (isLoading) return

    if (pagination?.total === 0) {
      setTotalPages(1)
      return
    }

    setTotalPages((prev) => {
      const total = Math.ceil(
        (pagination?.total || 0) / (pagination?.limit || 1) || 1
      )

      return total !== prev ? total : prev
    })
  }, [isLoading, pagination, startDate, deviceType])

  useEffect(() => {
    if (pagination?.limit === pageSize) return

    setPagination({
      limit: pageSize,
      offset: 0
    })
  }, [deviceType, pagination?.limit, pageSize, setPagination])

  const page =
    Math.floor((pagination?.offset || 0) / (pagination?.limit || 1)) + 1

  const onNextClick = () => {
    if (!pagination.hasNext && page < totalPages) return

    setPage(page + 1)
  }

  const onPrevClick = () => {
    if (!pagination.hasPrevious && page > 1) return

    setPage(page - 1)
  }

  const title = `${t('data-tables.page')} ${page}/${totalPages}`

  const navigation = {
    title,
    disabled: !pagination.hasNext && !pagination.hasPrevious,
    disabledArrow: getDisabledArrow(pagination.hasPrevious, pagination.hasNext),
    onNextClick,
    onPrevClick,
    currentIndex: page,
    orientation: 'vertical' as const,
    textVariant: 'content-emphasis-04' as const
  }

  return {
    data,
    isLoading,
    navigation,
    shouldDisplayPagination: totalPages > 1
  }
}

export default usePaginatedMonthlyEvents
